import {
  CardFundingType,
  CardOptionsConfig,
  CardType,
  Constants,
  WidgetNames,
} from '@finxone-platform/shared/sys-config-types';

// The nested card panel keys carry the account the card sits on, matching the key the cards
// widget looks up at runtime (see `getNestedCardType`): prepaid cards belong to a top-up
// account, debit cards to a current account. Credit cards have no account suffix.
const cardAccountSuffix: Record<string, string> = {
  [CardFundingType.PREPAID]: 'TopUp',
  [CardFundingType.DEBIT]: 'Current',
  [CardFundingType.CREDIT]: '',
};

// Makes a card key from its funding type and type, e.g. `prepaidVirtualTopUp`, `debitPhysicalCurrent`.
// e.g. (`prepaid`, `virtual`) -> `prepaidVirtualTopUp`, (`debit`, `physical`) -> `debitPhysicalCurrent`.
export const buildCardNestedWidgetKey = (
  fundingType: CardFundingType | string,
  type: CardType | string,
): string =>
  `${fundingType}${type.charAt(0).toUpperCase()}${type.slice(1)}${cardAccountSuffix[fundingType] ?? ''}`;

// Only these keys are ever hidden; any other panel key stays visible.
export const cardCombinationKeys: ReadonlySet<string> = new Set<string>(
  [CardFundingType.PREPAID, CardFundingType.DEBIT, CardFundingType.CREDIT].flatMap((fundingType) =>
    [CardType.VIRTUAL, CardType.PHYSICAL].map((type) => buildCardNestedWidgetKey(fundingType, type)),
  ),
);

// Returns only the card keys that should be shown for a project.
// A card combination (e.g. `debitVirtualCurrent`) is shown only if that card exists in `cardOptions`.
// Other keys like `noCard` are always shown.
export const getAllowedCardNestedKeys = (
  cardOptions: CardOptionsConfig | undefined | null,
  nestedKeys: Iterable<string>,
): Set<string> => {
  const configuredKeys = new Set<string>();
  Object.values(cardOptions ?? {}).forEach((option) => {
    if (option?.type && option?.fundingType) {
      configuredKeys.add(buildCardNestedWidgetKey(option.fundingType, option.type));
    }
  });

  const allowed = new Set<string>();
  for (const key of nestedKeys) {
    // Drop a card combination the project has not configured; keep everything else.
    if (cardCombinationKeys.has(key) && !configuredKeys.has(key)) continue;
    allowed.add(key);
  }
  return allowed;
};

const defaultCardStylingOptions = {
  defaultCardTemplate: `<div class="is-vertical-align justify-content-between h-100 ml-3">
  <div class="mt-3">
    <div class="typog-xs">{{ card.name }}</div>
    <div class="typog-xxs">{{ card.expiry }}</div>
  </div>
  <div class="mb-3 typog-xl font-weight-medium">{{ 'capitaliseFirstLetter' card.type}} Card</div>
</div>`,
  pendingCardTemplate: `<div class="is-vertical-align justify-content-between h-100 ml-3">
  <div class="mt-3 mr-3 is-space-between w-100">
    <div>
      <div class="typog-xs">{{ card.name }}</div>
      <div class="typog-xxs">{{ card.expiry }}</div>
    </div>
    <button type="button" class="typog-xs textOnSurfaceMuted surface-color" id="activate-card">Activate Now</button>
  </div>
  <div class="mb-3 typog-xl font-weight-medium">{{ 'capitaliseFirstLetter' card.type}} Card</div>
</div>`,
  frozenCardTemplate: `<div class="is-center is-vertical-align frozen-gradient frozen-blur h-100 fade-in">
    <div>
      <svg width="32px" height="32px">
  <use xlink:href="assets/icon-library/material/mat_frozen.svg#icon-frozen"></use>
  </svg>
  </div>
  <div>Frozen</div>
  </div>`,
  useGradient: true,
  imagePath: '',
};

const defaultCardCssConfig = {
  backGroundColor: '#0000ff',
  backgroundGradient: 'linear-gradient(106.06deg, #000B0C 1.1%, #3E3E3E 47.43%, #000B0C 100%)',
  width: 100,
  height: 175,
  widthDimensionUnit: '%',
  heightDimensionUnit: 'px',
  borderWidth: 0,
};

export const cardsNestedWidgets = {
  // prepaidVirtualTopUp
  [buildCardNestedWidgetKey(CardFundingType.PREPAID, CardType.VIRTUAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Prepaid Virtual Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // prepaidPhysicalTopUp
  [buildCardNestedWidgetKey(CardFundingType.PREPAID, CardType.PHYSICAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Prepaid Physical Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // debitVirtualCurrent
  [buildCardNestedWidgetKey(CardFundingType.DEBIT, CardType.VIRTUAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Debit Virtual Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // debitPhysicalCurrent
  [buildCardNestedWidgetKey(CardFundingType.DEBIT, CardType.PHYSICAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Debit Physical Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // creditPhysical
  [buildCardNestedWidgetKey(CardFundingType.CREDIT, CardType.PHYSICAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Credit Physical Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // creditVirtual
  [buildCardNestedWidgetKey(CardFundingType.CREDIT, CardType.VIRTUAL)]: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'Credit Virtual Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          ...defaultCardStylingOptions,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        ...defaultCardCssConfig,
      },
    },
  },
  // No Card (shown when no cards are registered to the account)
  noCard: {
    name: WidgetNames.CARD,
    isRemovable: true,
    widgetConfigDisplayName: 'No Card',
    attributes: {
      widgetProperties: {
        textContent: '',
        cardStylingOptions: {
          useGradient: false,
          defaultCardTemplate: `<div class="is-vertical-align w-100 h-100">
<div class="typog-s d-flex mt-2 mr-2 is-right">+ Add Card</div>
<p class=" is-center h-100 mb-5 ">No Cards are registered to this account</p>
</div>`,
        },
      },
      elementsConfig: [],
      css_config: {
        ...Constants.defaultUiNestedCardCssConfig,
        width: 100,
        height: 175,
        widthDimensionUnit: '%',
        heightDimensionUnit: 'px',
        borderColor: 'var(--color-on-background-muted)',
        color: 'var(--color-on-background-muted)',
        borderWidth: 2,
        backGroundColor: 'var(--color-background)',
      },
    },
  },
};
